// Shared building blocks for Dr. Mavroson's concierge programs (Advanced
// Peptide, Advanced Weight Loss Support, Advanced Longevity & Functional
// Medicine). None of these are Beluga visits: they submit to the practice's
// own async-telehealth workflow (docs/BACKEND_CONCIERGE_PROGRAMS_PRD.md).
//
// Each program's data file composes its flow from these steps plus its own
// program-specific questions, and reuses the helpers at the bottom. Relative
// imports so Jest can load this without the `@/` alias.
import { US_STATES } from './usStates';
import {
  CONSENT_VERSION,
  CONSENT_TREATMENT_PRIVACY_BODY,
  CONSENT_PEPTIDE_THERAPY_BODY,
  CONSENT_FINANCIAL_MEMBERSHIP_BODY,
  AGREEMENT_URL,
} from './advancedPeptideConsent';
import { consentAnswerLabel, formatMultiSelectAnswer, splitHeightWeightAnswer, sha256Hex } from '../lib/utils';

export { CONSENT_VERSION };

// New York only for now (user decision 2026-09-27). Everyone else → waitlist.
// The backend owns the live list (GET /tenant/config → conciergePrograms
// .servedStates, BE PR #321); TenantBranding calls setServedStates with it and
// this constant is the fallback while the flag is off or the fetch fails.
export const SERVED_STATES = ['NY'];
let servedStates = SERVED_STATES;
export const setServedStates = (list) => {
  const clean = Array.isArray(list) ? list.map((s) => String(s || '').toUpperCase()).filter((s) => /^[A-Z]{2}$/.test(s)) : [];
  servedStates = clean.length ? clean : SERVED_STATES;
};
export const getServedStates = () => servedStates;
export const isStateServed = (code) => servedStates.includes(String(code || '').toUpperCase());

export const PRACTICE = {
  name: 'Mavroson Medical, P.C., d/b/a Dr. SkinnyShot / ReNu Medical',
  practitioner: 'Dr. Matt Mavroson',
  practitionerShort: 'Dr. Mavroson',
  alertEmail: 'DrMatt@doctorskinnyshot.com',
};
// The practice's own secondary consent (Jotform), shown in a pop-up on the
// completion screen once payment has gone through. Owner request 2026-10-19.
export const SECONDARY_CONSENT_URL = 'https://form.jotform.com/262395516625059';
// Portrait from the practice's own About page (fuscocare.com/about-us).
export const PRACTITIONER_IMAGE = '/programs/team/matthew-mavroson.png';

export const NOT_SERVED_REASON =
  'Unfortunately, we are currently unable to serve you. We do not administer therapy for minors, pregnant or breastfeeding women, or with certain contraindications for all protocols, such as active cancer and diabetes.';

// ── Shared steps ─────────────────────────────────────────────────────────────
export const ageConfirmQuestion = {
  id: 'age-confirm', type: 'single-select', question: 'Are you 18 or older?',
  options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no', disqualifies: true }],
};
export const stateOptions = [
  ...US_STATES.filter((s) => s.value === 'NY'),
  ...US_STATES.filter((s) => s.value && s.value !== 'NY'),
];
export const stateQuestion = {
  id: 'state-of-residence', type: 'single-select', question: 'What state do you live in?',
  subtitle: 'Telehealth rules depend on where you are located.', options: stateOptions,
};
export const waitlistStep = {
  id: 'ap-waitlist', type: 'waitlist', question: "We're not in your state yet",
  subtitle: "We're currently serving patients in New York only, but we're expanding. Join the waitlist to get launch updates in your state.",
};
export const sexAtBirthQuestion = {
  id: 'sex-at-birth', type: 'single-select', question: 'What is your sex assigned at birth?',
  options: [{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }],
};
export const pregnancyQuestion = {
  id: 'pregnancy-status', type: 'single-select',
  question: 'Are you currently pregnant, breastfeeding or planning to become pregnant?',
  options: [{ label: 'Yes', value: 'yes', disqualifies: true }, { label: 'No', value: 'no' }],
};
export const heightWeightQuestion = {
  id: 'height-weight', type: 'height-weight-bmi', question: 'What is your height and weight?',
  subtitle: 'Used only to personalize your plan.', skipBmiCheck: true,
};
export const cancerQuestion = {
  id: 'cancer-history', type: 'single-select', question: 'Do you have, or have you ever had, cancer?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes, active cancer', value: 'active', disqualifies: true },
    { label: 'Yes, cancer in remission', value: 'remission' },
  ],
};
// Which diabetes answers hard-stop is per program (Luma stops T1/T2; the
// weight-management and longevity programs treat type 2 patients).
export const diabetesQuestion = {
  id: 'diabetes', type: 'single-select', question: 'Do you have diabetes?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Pre-diabetes', value: 'prediabetes' },
    { label: 'Type 1', value: 'type1' },
    { label: 'Type 2', value: 'type2' },
  ],
};
// Luma's checklist + GLP-1 items Luma lacks. `flag` = listed in the consent's
// contraindications → sent to the practitioner as a review flag (not a stop).
export const conditionOptions = [
  { label: 'None of these apply to me', value: 'none' },
  { label: 'Pancreatitis', value: 'pancreatitis', flag: true },
  { label: 'Kidney problems', value: 'kidney-problems' },
  { label: 'Liver problems', value: 'liver-problems' },
  { label: 'Cardiovascular disease', value: 'cardiovascular-disease', flag: true },
  { label: 'Gastrointestinal problems', value: 'gi-problems', flag: true },
  { label: 'Gastroparesis', value: 'gastroparesis', flag: true },
  { label: 'Bipolar disorder or severe anxiety/depression', value: 'psychiatric', flag: true },
  { label: 'Severe constipation', value: 'severe-constipation' },
  { label: 'Family history of Multiple Endocrine Neoplasia (MEN)', value: 'men-family', flag: true },
  { label: 'Family history of Medullary Thyroid Carcinoma (MTC)', value: 'mtc-family', flag: true },
  { label: 'Thyroid tumors', value: 'thyroid-tumors', flag: true },
  { label: 'Hypothyroidism, hyperthyroidism or other thyroid issues', value: 'thyroid-issues', flag: true },
  { label: 'Benign tumors', value: 'benign-tumors' },
  { label: 'High blood pressure', value: 'high-blood-pressure' },
  { label: "Wilson's disease", value: 'wilsons-disease' },
  { label: 'Autoimmune disease', value: 'autoimmune' },
  { label: 'Pancreatic cancer', value: 'pancreatic-cancer', flag: true },
  { label: 'Triglycerides over 600 at any point', value: 'high-triglycerides' },
  { label: 'Current gallbladder disease or symptomatic gallstones', value: 'gallbladder-disease', flag: true },
  { label: 'Anorexia or bulimia', value: 'eating-disorder', flag: true },
  { label: 'Other significant health challenge', value: 'other' },
];
export const conditionHistoryQuestion = {
  id: 'condition-history', type: 'multi-select', question: 'Do you have, or have you had, any of the following?',
  subtitle: 'Select all that apply. Your answers are confidential and protected by HIPAA.', options: conditionOptions,
};
// Id chosen so the medical-term autocomplete (conditions domain) applies.
export const conditionDetailsQuestion = {
  id: 'current-medical-condition', type: 'textarea',
  question: 'Please briefly describe your history with the conditions you selected',
  subtitle: 'Include when diagnosed, current treatment, and any surgeries',
  placeholder: 'e.g., High blood pressure since 2019, controlled with lisinopril...', required: true,
};
export const previousTreatmentQuestions = (question, subtitle) => ([
  { id: 'previous-peptide-treatment', type: 'single-select', question, subtitle, options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }] },
  { id: 'previous-reaction', type: 'single-select', question: 'Did you experience any allergies or negative reactions to any of these?', options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }] },
  { id: 'previous-reaction-details', type: 'textarea', question: 'Please tell us more about what you used, when, and what you experienced', placeholder: 'e.g., Semaglutide in 2025 — nausea for the first month...', required: true },
]);
export const medicationsQuestion = {
  id: 'medications-list', type: 'textarea', question: 'Please list all medications and supplements you are currently taking',
  subtitle: 'Include prescription medications, over-the-counter drugs, and supplements. Type "None" if you take nothing.',
  placeholder: 'e.g., Metformin 500mg twice daily, Lisinopril 10mg daily, Vitamin D 2000 IU daily...', required: true,
};
export const interactingMedicationOptions = [
  { label: 'Muscarinic antagonists (e.g. atropine)', value: 'muscarinic-antagonist', disqualifies: true },
  { label: 'Somatostatin-containing medications (e.g. clonidine, levodopa)', value: 'somatostatin', disqualifies: true },
  { label: 'Propylthiouracil', value: 'propylthiouracil', disqualifies: true },
  { label: 'None of the above', value: 'none' },
];
export const interactingMedicationsQuestion = {
  id: 'interacting-medications', type: 'multi-select', question: 'Do you take any of the following medications?',
  subtitle: 'Select all that apply', options: interactingMedicationOptions,
};
export const allergiesQuestion = {
  id: 'allergies', type: 'textarea', question: 'Do you have any drug or food allergies?',
  subtitle: 'Include drug allergies, food allergies, and environmental allergies. Type "None" if you have no allergies.',
  placeholder: 'e.g., Penicillin (causes rash), Shellfish (anaphylaxis)...', required: true,
};
export const bloodworkQuestion = {
  id: 'bloodwork-interest', type: 'single-select', question: 'Would you be interested in at-home bloodwork?',
  subtitle: 'Baseline and follow-up labs help your provider dose safely.',
  options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }],
};
export const additionalQuestionsQuestion = {
  id: 'additional-questions', type: 'textarea', question: `What other information or questions do you have for ${PRACTICE.practitionerShort}?`,
  subtitle: 'Feel free to share any concerns or questions', placeholder: 'Type your questions or concerns here...',
};
export const basicContactQuestion = {
  id: 'basic-contact', type: 'basic-contact', question: 'How can we reach you?',
  subtitle: `By continuing, you consent to receive text messages from ${PRACTICE.name} regarding your medical care. Message and data rates may apply. Reply STOP to opt out at any time.`,
};
export const contactPreferenceQuestion = {
  id: 'contact-preference', type: 'single-select', question: 'How should we reach out regarding next steps?',
  options: [{ label: 'Text', value: 'text' }, { label: 'Email', value: 'email' }, { label: 'Either', value: 'either' }],
};
export const emergencyContactStep = { id: 'emergency-contact', type: 'emergency-contact', question: 'Emergency contact', subtitle: 'Someone we can reach if we ever need to.' };
export const shippingAddressStep = { id: 'shipping-address', type: 'shipping-address', question: 'Your address', subtitle: 'Used for your medical record and any medication shipments.' };
export const identityVerificationStep = {
  id: 'identity-verification', type: 'file-upload', question: 'Identity Verification',
  subtitle: `Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible.

If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card.

Make sure the photo is clear and legible.`,
};

const consentOptions = [
  { label: 'I have read and understand this section and wish to proceed', value: 'consent' },
  { label: 'I do not wish to continue', value: 'decline', disqualifies: true },
];
export const consentSteps = [
  { id: 'consent-treatment-privacy', type: 'consent-dropdown', question: 'Consent to Treatment, Privacy & Telehealth', consentText: CONSENT_TREATMENT_PRIVACY_BODY, consentVersion: CONSENT_VERSION, options: consentOptions },
  { id: 'consent-peptide-therapy', type: 'consent-dropdown', question: 'Informed Consent for Weight Loss and Peptide Therapy', consentText: CONSENT_PEPTIDE_THERAPY_BODY, consentVersion: CONSENT_VERSION, options: consentOptions },
  { id: 'consent-financial-membership', type: 'consent-dropdown', question: 'Financial Agreement & Membership Terms', consentText: CONSENT_FINANCIAL_MEMBERSHIP_BODY, consentVersion: CONSENT_VERSION, options: consentOptions },
  {
    id: 'final-verification', type: 'consent-dropdown', question: 'Patient Verification',
    consentText: `I have reviewed, understand, and attest that the information I have provided above is true to the best of my knowledge. I understand that it is critical to my health to share complete health information with my provider. I will not hold ${PRACTICE.practitioner} or ${PRACTICE.name} responsible for any oversights or omissions, whether intentional or not, in the information that I provided above.

For medical questions, message your practitioner through your patient dashboard. For questions about billing or your membership, contact the practice.`,
    consentVersion: CONSENT_VERSION,
    checkbox: {
      text: 'I have read and agree to the Consent to Treat, Peptide Therapy, and Financial Agreement and the Terms of Service.',
      links: [
        { text: 'Consent to Treat, Peptide Therapy, and Financial Agreement', url: AGREEMENT_URL },
        { text: 'Terms of Service', url: 'https://fitpal.com/terms-and-conditions/' },
      ],
    },
    options: [{ label: 'I consent and wish to move forward', value: 'consent' }, { label: 'I do not wish to continue', value: 'decline', disqualifies: true }],
  },
];
export const CONSENT_STEP_IDS = consentSteps.map((s) => s.id);
export const checkoutStep = { id: 'checkout', type: 'checkout', question: 'Start your membership' };
export const completeStep = { id: 'complete', type: 'complete', question: 'Complete' };

// The tail every program shares: contact → consents → checkout.
export const closingSteps = [
  basicContactQuestion, contactPreferenceQuestion, emergencyContactStep, shippingAddressStep, identityVerificationStep,
  ...consentSteps, checkoutStep, completeStep,
];

// ── Shared flow logic ────────────────────────────────────────────────────────
// A non-served state cuts the flow after the state question and appends the
// waitlist dead end. Returns null when the state is fine.
export const stateGate = (flow, answers) => {
  const state = answers['state-of-residence'];
  if (!state || isStateServed(state)) return null;
  const i = flow.findIndex((q) => q.id === 'state-of-residence');
  return [...flow.slice(0, i + 1), waitlistStep];
};

// Branches every program has (drop-only, never reorders).
export const applyCommonBranches = (flow, answers) => {
  let f = flow;
  if (answers['sex-at-birth'] === 'male') f = f.filter((q) => q.id !== 'pregnancy-status');
  const conditions = answers['condition-history'];
  const hasConditions = Array.isArray(conditions) && conditions.some((c) => c && c !== 'none');
  if (!hasConditions) f = f.filter((q) => q.id !== 'current-medical-condition');
  if (answers['previous-peptide-treatment'] !== 'yes') f = f.filter((q) => !['previous-reaction', 'previous-reaction-details'].includes(q.id));
  if (answers['previous-reaction'] !== 'yes') f = f.filter((q) => q.id !== 'previous-reaction-details');
  return f;
};

// Hard stops every program shares. `diabetesStops` lists the diabetes values
// that stop for this program (e.g. ['type1','type2'] for peptides).
export const commonDisqualify = (questionId, answer, { diabetesStops = [] } = {}) => {
  if (questionId === 'age-confirm' && answer === 'no') return { disqualified: true, reason: NOT_SERVED_REASON };
  if (questionId === 'pregnancy-status' && answer === 'yes') return { disqualified: true, reason: NOT_SERVED_REASON };
  if (questionId === 'cancer-history' && answer === 'active') return { disqualified: true, reason: NOT_SERVED_REASON };
  if (questionId === 'diabetes' && diabetesStops.includes(answer)) return { disqualified: true, reason: NOT_SERVED_REASON };
  if (questionId === 'interacting-medications' && Array.isArray(answer)) {
    const bad = interactingMedicationOptions.filter((o) => o.disqualifies).map((o) => o.value);
    if (answer.some((v) => bad.includes(v))) {
      return { disqualified: true, reason: 'Based on your current medications, you may not be a candidate for this program. The medications you selected may interact with growth-hormone-releasing peptides. Please consult with your healthcare provider.' };
    }
  }
  // Defensive: the state gate ran earlier, but the address step has its own
  // state select — an out-of-state address can't reach payment.
  if (questionId === 'shipping-address' && answer && answer.state && !isStateServed(answer.state)) {
    return { disqualified: true, reason: "We're currently serving patients in New York only. Your address must be in New York to continue." };
  }
  if (CONSENT_STEP_IDS.includes(questionId)) {
    const v = answer && typeof answer === 'object' && 'option' in answer ? answer.option : answer;
    if (v === 'decline') return { disqualified: true, reason: 'You have indicated that you do not wish to continue with this treatment.' };
  }
  return { disqualified: false };
};

// Contraindication flags for the practitioner (from the consent's list).
export const flaggedConditions = (answers = {}) => {
  const picked = Array.isArray(answers['condition-history']) ? answers['condition-history'] : [];
  const flags = conditionOptions.filter((o) => o.flag && picked.includes(o.value)).map((o) => o.label);
  if (answers['cancer-history'] === 'remission') flags.push('Cancer in remission');
  if (answers['diabetes'] === 'prediabetes') flags.push('Pre-diabetes');
  if (answers['diabetes'] === 'type1') flags.push('Type 1 diabetes');
  if (answers['diabetes'] === 'type2') flags.push('Type 2 diabetes');
  return flags;
};

// {question, answer, slide_id, rawAnswer} per answered step, in flow order, for
// the submit body. Consent steps also carry consentVersion.
export const formatAnswersForSubmit = (answers = {}, flow = []) =>
  flow.flatMap((slide) => {
    const answer = answers[slide.id];
    if (answer === undefined || answer === null || answer === '') return [];
    const split = splitHeightWeightAnswer(answer);
    if (split) return split;
    const consentLabel = consentAnswerLabel(slide, answer);
    let formatted;
    if (consentLabel !== null) formatted = consentLabel;
    else if (Array.isArray(answer)) formatted = formatMultiSelectAnswer(answer);
    else if (typeof answer === 'object') {
      if (answer.preview) formatted = `File: ${answer.fileName || 'uploaded'}`;
      else formatted = JSON.stringify(answer);
    } else formatted = String(answer);
    const row = { question: slide.question, answer: formatted, slide_id: slide.id, rawAnswer: answer };
    if (slide.consentVersion) row.consentVersion = slide.consentVersion;
    return [row];
  });

export const consentsForSubmit = (answers = {}, acceptedAt = new Date().toISOString()) =>
  CONSENT_STEP_IDS.filter((id) => {
    const a = answers[id];
    const v = a && typeof a === 'object' && 'option' in a ? a.option : a;
    return v === 'consent';
  }).map((id) => ({ id, version: CONSENT_VERSION, acceptedAt }));

// The exact text each consent step showed, keyed by step id — hashed into
// the consent record so the backend can prove which wording was accepted
// (BE PR #318 stores `bodySha256` when the FE sends it; null otherwise).
export const CONSENT_BODIES = Object.fromEntries(consentSteps.map((s) => [s.id, s.consentText]));
export const withConsentHashes = async (consents = []) =>
  Promise.all(
    consents.map(async (c) => {
      const hash = CONSENT_BODIES[c.id] ? await sha256Hex(CONSENT_BODIES[c.id]) : null;
      return hash ? { ...c, bodySha256: hash } : c;
    })
  );
